import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Heart, Gift, Star } from "lucide-react";

/**
 * Hero section for the public homepage — headline, promo banner, CTA buttons,
 * VIP loyalty callout and the hero image.
 */
export default function PublicHero({ settings, salonSlug }) {
  const salonName = (settings.salon_name || '').trim();
  const heroTag = settings.hero_tag || (salonName ? `Welcome to ${salonName}` : 'Welcome');
  const heroHeadline = settings.hero_headline || salonName || 'Book your appointment';
  const heroLocation = settings.hero_headline_location || '';
  const heroDesc = settings.hero_description ||
    (salonName
      ? `Book your appointment at ${salonName} online — instant confirmation.`
      : 'Book your appointment online — instant confirmation.');
  const promoEnabled = settings.new_client_promo_enabled !== false;
  const promoText = settings.new_client_promo_text || `£${settings.new_client_promo_amount ?? 5} off for new clients!`;
  const promoSub = settings.new_client_promo_sub || 'Discount applied automatically at checkout';

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 opacity-30" style={{
        background: 'radial-gradient(60% 60% at 20% 0%, rgba(248,189,212,0.55) 0%, rgba(255,247,250,0.0) 60%), radial-gradient(40% 40% at 90% 10%, rgba(255,217,200,0.45) 0%, rgba(255,247,250,0.0) 55%)',
      }} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-secondary rounded-full text-xs font-medium text-secondary-foreground mb-4">
              <Heart size={12} className="text-primary" />
              {heroTag}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Playfair Display' }}>
              {heroHeadline}{' '}
              <span className="text-primary">{heroLocation}</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mb-6 max-w-md">{heroDesc}</p>

            {promoEnabled && (
              <div className="mb-6 inline-flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-2xl">
                <span className="text-lg" role="img" aria-label="celebration">🎉</span>
                <div>
                  <p className="text-sm font-semibold text-green-800" style={{ fontFamily: 'Playfair Display' }}>{promoText}</p>
                  <p className="text-xs text-green-600">{promoSub}</p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <Link to={`/book`}>
                <Button data-testid="hero-book-button" size="lg" className="rounded-xl text-base px-8 h-12 hover:-translate-y-0.5 transition-transform duration-200">
                  Book your nail appointment <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/gift-vouchers">
                <Button variant="outline" size="lg" className="rounded-xl text-base px-6 h-12 hover:-translate-y-0.5 transition-transform duration-200">
                  <Gift size={18} className="mr-2" /> Buy a gift voucher
                </Button>
              </Link>
            </div>

            {/* VIP Loyalty Callout */}
            <div className="mt-5 relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border border-amber-200/50 p-4 max-w-md">
              <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-20 bg-amber-300" />
              <div className="relative flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                  <Star size={18} className="text-amber-500 fill-amber-400" />
                </div>
                <div>
                  <p className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>VIP Rewards Programme</p>
                  <p className="text-xs text-amber-800/70 mt-1 leading-relaxed">
                    Earn VIP status after {settings.vip_threshold || 10} visits and unlock rewards every {settings.vip_reward_interval || 10} appointments. Book today to start your journey!
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:block">
            <Card className="rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(43,18,27,0.12)] rotate-1 hover:rotate-0 transition-transform duration-300">
              <img
                src={settings.hero_image_url || "https://images.unsplash.com/photo-1604654894610-df63bc536371?w=600&h=400&fit=crop"}
                alt={settings.salon_name ? `${settings.salon_name} salon` : 'Beauty salon hero'}
                className="w-full h-80 object-cover"
                loading="eager"
              />
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
